import React from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { useToast } from "../context/ToastContext";
import { getTeamCalendarUrl } from "../config/api";
import { Team } from "../types/models";

/** How long the Copy button shows its confirmation before resetting. */
const COPIED_FEEDBACK_MS = 2000;

interface TeamCalendarModalProps {
  team: Team;
  show: boolean;
  onHide: () => void;
}

/**
 * Offers two ways to get a team's schedule into a calendar app:
 *
 * - Subscribe: copy the feed URL into Google Calendar, Apple Calendar or
 *   Outlook, which re-fetch it periodically so schedule changes show up
 *   without any action from the subscriber.
 * - Download: save a one-time copy of the same feed that won't update.
 */
export default function TeamCalendarModal({
  team,
  show,
  onHide,
}: TeamCalendarModalProps) {
  const toast = useToast();
  const calendarUrl = getTeamCalendarUrl(team.id);
  const urlInputRef = React.useRef<HTMLInputElement>(null);
  const [copied, setCopied] = React.useState(false);

  // Reset the Copy confirmation after a moment; cleared if the modal unmounts.
  React.useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      // navigator.clipboard is undefined outside secure contexts.
      if (!navigator.clipboard) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(calendarUrl);
      setCopied(true);
    } catch {
      // Leave the URL selected so it can be copied by hand.
      urlInputRef.current?.select();
      toast.showToast(
        "Couldn't copy automatically. The URL is selected so you can copy it.",
        "warning",
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Subscribe to this team's schedule</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-3">
          Add this URL to Google Calendar, Apple Calendar or Outlook. New games
          and schedule changes show up in your calendar automatically.
        </p>

        <Form.Group controlId="team-calendar-url">
          <Form.Label className="fw-semibold">Calendar URL</Form.Label>
          <InputGroup>
            <Form.Control
              ref={urlInputRef}
              type="text"
              value={calendarUrl}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button variant="primary" onClick={handleCopy}>
              <i
                className={`bi ${copied ? "bi-check2" : "bi-clipboard"} me-1`}
                aria-hidden="true"
              />
              {copied ? "Copied" : "Copy"}
            </Button>
          </InputGroup>
          <Form.Text className="text-muted">
            In Google Calendar, open Other calendars, choose +, then From URL.
            Calendar apps check for changes every few hours.
          </Form.Text>
        </Form.Group>

        <hr />

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span className="text-muted small">
            Or download a one-time copy. It won't update.
          </span>
          {/* The API serves the feed as an attachment, so following the link
              downloads the file without leaving the page. */}
          <Button
            as="a"
            href={calendarUrl}
            download
            variant="outline-secondary"
          >
            <i className="bi bi-download me-1" aria-hidden="true" />
            Download
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
